import { Location } from '@angular/common';
import {
  ActivatedRouteSnapshot,
  ActivationEnd,
  NavigationEnd,
  NavigationExtras,
  NavigationStart,
  Params,
  Router,
  UrlCreationOptions,
  UrlTree,
} from '@angular/router';
import { BehaviorSubject, filter, Observable, take } from 'rxjs';

import { ScrollRestoration } from './scroll-restoration.service';

type RouteParams<F extends (...args: unknown[]) => string> = (...args: Parameters<F>) => string;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RouteDefinition = (...args: any[]) => string;

interface Routes {
  [key: string]: RouteDefinition;
}

export type ParsedRoutes<T extends Routes> = {
  [key in keyof T]: RouteParams<T[key]>;
};

export class RouterBehavior {
  static router: Router;
  static previousUrl: string;
  static navigationState: { [k: string]: unknown };

  private static routeDataSubj = new BehaviorSubject<BaseRouteData>({});
  private static defaultReuse: (future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot) => boolean;
  private static trigger?: 'imperative' | 'popstate' | 'hashchange';
  private static location: Location;

  static get isReloadStrategy(): boolean {
    return this.router.routeReuseStrategy.shouldReuseRoute !== this.defaultReuse;
  }

  static get routeData(): BaseRouteData {
    return this.routeDataSubj.value;
  }

  static get navigationTrigger(): 'imperative' | 'popstate' | 'hashchange' | undefined {
    return this.trigger;
  }

  static get isImperative(): boolean {
    return this.trigger === 'imperative';
  }

  static setup(router: Router, location: Location, scrollRestoration?: ScrollRestoration): void {
    this.router = router;
    this.location = location;
    if (scrollRestoration) {
      scrollRestoration.setup();
    }
    this.defaultReuse = router.routeReuseStrategy.shouldReuseRoute;
    this.observeEvents();
  }

  static reloadReuseStrategy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  static defaultReuseStrategy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.defaultReuse;
  }

  static onNavEnd(cb: () => void): void {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => cb());
  }

  static getRouteDataChanges<T = BaseRouteData>(): Observable<T> {
    return RouterBehavior.routeDataSubj.asObservable() as Observable<T>;
  }

  static clearQueryParam(currentParams: Params | string, removeKey: string): void {
    if (typeof currentParams === 'string') {
      currentParams = this.queryStringToObject(currentParams);
    }
    const params = { ...currentParams };
    if (!params[removeKey]) {
      return;
    }
    const path = this.location.path().split('?')[0];
    const query: string[] = [];
    delete params[removeKey];
    Object.keys(params).forEach(key => {
      query.push(`${key}=${params[key]}`);
    });
    this.location.replaceState(path, query.join('&'));
  }

  static queryStringToObject(url: string): Params {
    return [...new URLSearchParams(url.split('?')[1])].reduce(
      (a, [k, v]) => (((a as { [key: string]: unknown })[k] = v), a),
      {},
    );
  }

  private static observeEvents(): void {
    const isPrimaryOutlet = (snapshot: ActivatedRouteSnapshot) =>
      snapshot.outlet === 'primary' && snapshot.parent?.outlet === 'primary';
    let snapshotData: Record<string, unknown> = {};
    let navStartUrl = '';
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.trigger = event.navigationTrigger;
        snapshotData = {};
        navStartUrl = this.router.url;
      }
      // we provide snapshot only of primary outlet of application routing
      if (event instanceof ActivationEnd && isPrimaryOutlet(event.snapshot)) {
        // aggregate snapshot date through all router levels
        snapshotData = Object.assign(snapshotData, event.snapshot.data || {});
        this.routeDataSubj.next(snapshotData);
      }
      // set snapshotData on activation end to prevent UI flickering
      if (event instanceof NavigationEnd) {
        this.previousUrl = navStartUrl;
        this.routeDataSubj.next(snapshotData);
        this.navigationState = this.router.getCurrentNavigation()?.extras.state || {};
      }
    });
  }
}

/** Create UrlTree from string, same as default but use string instead string[] */
export function createUrlTree(url: string, extras?: UrlCreationOptions): UrlTree {
  const urlTree = RouterBehavior.router.createUrlTree([url], extras);
  // tslint:disable-next-line: no-string-literal
  delete urlTree.root.children['primaryModal'];
  return urlTree;
}

/**
 * Navigate by string url using NavigationExtras,
 * because Router allows only NavigationBehaviorOptions at navigateByUrl()
 */
export function navigateByUrl(
  url: string,
  extras?: NavigationExtras,
  sameUrlReload?: boolean,
  removeOutlet?: string,
): Promise<boolean> {
  if (url.includes('?')) {
    const queryString = url.split('?')[1];
    const urlParams = new URLSearchParams(queryString);
    const query = paramsToObject(urlParams.entries());
    url = url.replace(`?${queryString}`, '');
    if (extras && extras.queryParams) {
      extras.queryParams = { ...extras.queryParams, ...query };
    } else {
      extras = { queryParams: query };
    }
  }
  const urlTree = createUrlTree(url, extras);
  // close any modals on primary router navigation
  // tslint:disable-next-line: no-string-literal
  delete urlTree.root.children['primaryModal'];
  if (removeOutlet) {
    delete urlTree.root.children[removeOutlet];
  }
  if (sameUrlReload && !RouterBehavior.isReloadStrategy) {
    RouterBehavior.router.onSameUrlNavigation = 'reload';
    RouterBehavior.reloadReuseStrategy();
  }
  const navigation = RouterBehavior.router.navigateByUrl(urlTree, extras);
  if (sameUrlReload) {
    navigation.then(() => {
      RouterBehavior.defaultReuseStrategy();
      RouterBehavior.router.onSameUrlNavigation = 'ignore';
    });
  }
  return navigation;
}

function paramsToObject(entries: IterableIterator<[string, string]>) {
  const result: { [key: string]: string } = {};
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
}

export function createUrl(url: string, moduleRoot?: string): string {
  moduleRoot = moduleRoot ? `/${moduleRoot}/` : '/';
  return url ? `${moduleRoot}${url}` : moduleRoot.replace(/\/$/, '');
}

export function createNavigation<T extends Routes, R = ParsedRoutes<T>>(
  from: T,
  moduleRoot?: string,
  overrides?: Partial<ParsedRoutes<T>>,
): R {
  const createdRoutes: { [key: string]: unknown } = {};
  Object.keys(from).forEach(key => {
    createdRoutes[key] = (...args: unknown[]) =>
      createUrl(from[key].apply(null, args), moduleRoot || '/').replace('//', '/');
  });
  Object.assign(createdRoutes, overrides);
  return createdRoutes as unknown as R;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type paramExtractor<T> = (key: keyof T) => any;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractParam = <T>(key: keyof T): any => `:${key as string}`;
